import { table } from "console"
import * as React from "react"
import { DialogTrigger } from "@radix-ui/react-dialog"
import { VisibilityState } from "@tanstack/react-table"
import { DataSnapshot, remove } from "firebase/database"
import { checkDomainOfScale } from "recharts/types/util/ChartUtils"
import * as XLSX from "xlsx"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Header } from "@/components/header"
import { Icons } from "@/components/icons"

type TableHeader = {
  accessorKey: string
  header?: string | (() => JSX.Element)
  cell?: () => JSX.Element
}

type HeaderType = {
  key: string
}

export function ExportOperationModal({
  tableData,
  fileName,
  showExportModal,
  setShowExportModal,
}: {
  tableData: any
  fileName: string
  showExportModal: boolean
  setShowExportModal: React.Dispatch<React.SetStateAction<boolean>>
}) {
  // table header and data
  const [headers, setHeader] = React.useState<HeaderType[]>()
  const [filterData, setFilterData] = React.useState<any>()

  //console.log(headers)
  // export variable
  const [exportType, setExportType] = React.useState<string>("")

  React.useEffect(() => {
    if (tableData.length > 0) {
      const keys = Object.keys(tableData[0])
      setHeader(keys.map((key) => ({ key })))
      setFilterData(tableData)
    }
  }, [tableData])

  React.useEffect(() => {
    if (!headers) return

    const allowedKeys = headers.map((header) => header.key)

    setFilterData(() =>
      tableData.map((data: any) => {
        const filteredEntry: Record<string, any> = {}

        Object.keys(data).forEach((key) => {
          if (allowedKeys.includes(key)) {
            filteredEntry[key] = data[key]
          }
        })

        return filteredEntry
      }),
    )
  }, [headers])

  function handleRemovingHeader(headerkey: string) {
    setHeader(() => headers?.filter((header) => header.key != headerkey))
  }

  function handleAddingHeader(headerKey: string) {
    setHeader((prev: any) => {
      const updateHeader = [...prev, { key: headerKey }]

      return updateHeader
    })
  }

  return (
    <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
      <DialogTrigger
        className="flex items-center gap-4 rounded-md border border-primary bg-primary px-4 py-1 text-white"
        onClick={() => setShowExportModal(!showExportModal)}
      >
        <Icons.package />
        Export
      </DialogTrigger>
      <DialogContent>
        <DialogTitle>Export Meter Size</DialogTitle>
        <Card className="w-full p-4">
          <CardContent>
            <h1 className="mb-3 text-xl font-semibold">Filter columns</h1>
            {Array.isArray(tableData) &&
              tableData.length > 0 &&
              Object.keys(tableData[0])?.map((data: any, index: number) => {
                const isChecked = headers?.some((header) => data == header.key)

                return (
                  <div className="flex items-center gap-2" key={data.key}>
                    <Input
                      type="checkbox"
                      checked={isChecked}
                      className="h-4 w-4"
                      onChange={() =>
                        isChecked
                          ? handleRemovingHeader(data)
                          : handleAddingHeader(data)
                      }
                    />
                    <span>{data.replace(/_/g, " ")}</span>
                  </div>
                )
              })}

            <div className="mt-4">
              <Select onValueChange={(value) => setExportType(value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select export type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="xlsx">.xlsx</SelectItem>
                    <SelectItem value="application/json">.json</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <div className="mt-7 flex justify-end gap-2 *:px-10">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowExportModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => exportData(filterData, fileName, exportType)}
              >
                Export
              </Button>
            </div>
          </CardContent>
        </Card>
      </DialogContent>
    </Dialog>
  )
}

function exportData(filterData: any, fileName: string, exportType: string) {
  console.log(exportType)
  if (exportType == "application/json") {
    const blob = new Blob([JSON.stringify(filterData)], {
      type: "application/json",
    })

    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    ;(a.href = url), (a.download = `${fileName}.json`)
    a.click()

    URL.revokeObjectURL(url)
  } else if (exportType == "xlsx") {
    const worksheet = XLSX.utils.json_to_sheet(filterData)

    const workbook = XLSX.utils.book_new()

    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet 1")

    XLSX.writeFile(workbook, `${fileName}.${exportType}`)
  }
}
